import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { COMPANY_MANAGER_PROFILES, hasCurrentProfile } from "app/utils/appHelpers";
import deviceServices from "app/services/automations/device-services";
import { describeControlLoop } from "app/shared/automations/controlLoopDisplay";
import { LOCKOUT_REASONS, SOURCE_LABELS, clock, errorMessage, stateLabel } from "./actuatorDisplay";
import { formatTimeAgo } from "./deviceDisplay";

const MANUAL_MINUTES = 30;

// En una salida supervisada el control manual vence (vuelve a su estado seguro o al control); en una sin estado seguro
// solo impide que las reglas la cambien durante ese rato, y después queda como la dejaron.
// Operación de una salida (un circuito de un equipo actuador): qué pidió la plataforma, qué reporta el equipo y hasta
// cuándo se sostiene; los admins pueden encenderla o apagarla por un rato y fijar su estado seguro. El control
// «Mantener en rango» que la maneja y los límites de seguridad que la protegen son automatizaciones: aquí solo se
// muestran y se enlazan a Automatizaciones. Con un límite activo no se ofrece encenderla.
const ActuatorControlSection = ({ deviceId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { authUser } = useJumboAuth();
    const canOperate = hasCurrentProfile(authUser, COMPANY_MANAGER_PROFILES);
    const isSuperadmin = hasCurrentProfile(authUser, ["superadmin"]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!deviceId) return undefined;
        let active = true;
        setLoading(true);
        deviceServices.getActuator(deviceId)
            .then((response) => { if (active) { setStatus(response); setError(null); } })
            .catch(() => { if (active) setError("No se pudo cargar el estado de la salida."); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [deviceId]);

    useEffect(() => load(), [load]);

    const run = (request, fallbackError) => {
        setSaving(true);
        setError(null);
        return request()
            .then((response) => { setStatus((current) => ({ ...current, ...response })); return true; })
            .catch((requestError) => { setError(errorMessage(requestError, fallbackError)); return false; })
            .finally(() => setSaving(false));
    };

    if (loading && !status) return <Box sx={{ textAlign: "center", py: 2 }}><CircularProgress size={22} /></Box>;
    if (!status) return error ? <Alert severity="error">{error}</Alert> : null;

    const equipment = status.equipment || {};
    const loop = status.control_loop;
    const limits = status.safety_limits || [];
    const trippedLimit = limits.find((limit) => limit.tripped);
    const effective = status.effective_state;
    const backHere = { from: `${location.pathname}${location.search}` };
    // Solo los equipos con driver (hoy el WS558) se manejan por estado deseado: ahí un control puede sostener la salida.
    const loopCapable = status.controllable && !!equipment.has_driver;

    return (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Control de la salida</Typography>
                <Chip size="small" color={effective === "ON" ? "success" : "default"} label={stateLabel(effective)} />
            </Stack>

            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            {equipment.locked_out && (
                <Alert severity="error" sx={{ mt: 1 }}
                       action={isSuperadmin && (
                           <Button color="inherit" size="small" disabled={saving}
                                   onClick={() => run(() => deviceServices.clearActuatorLockout(deviceId), "No se pudo liberar el equipo.")}>
                               Liberar
                           </Button>
                       )}>
                    El equipo está bloqueado por seguridad ({LOCKOUT_REASONS[equipment.lockout_reason] || equipment.lockout_reason}).
                    Todo queda en su estado seguro hasta que alguien lo revise.
                </Alert>
            )}
            {trippedLimit && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    Límite «{trippedLimit.name}» activo{trippedLimit.trip_reason ? `: ${trippedLimit.trip_reason}` : ""}. La salida no se puede encender hasta que vuelva a la normalidad.
                </Alert>
            )}
            {!equipment.downlinks_enabled && (
                <Alert severity="info" sx={{ mt: 1 }}>Este equipo todavía no recibe órdenes desde la plataforma.</Alert>
            )}

            <Stack spacing={0.4} sx={{ mt: 1 }}>
                <Typography variant="body2">
                    Pedido: <b>{stateLabel(status.desired_state)}</b>
                    {status.desired_source && ` · por ${SOURCE_LABELS[status.desired_source] || status.desired_source} desde las ${clock(status.desired_state_at)}`}
                </Typography>
                <Typography variant="body2" color={status.reported && status.reported.state !== effective ? "warning.main" : "text.primary"}>
                    El equipo reporta: <b>{status.reported ? stateLabel(status.reported.state) : "sin datos"}</b>
                    {status.reported && ` ${formatTimeAgo(status.reported.time)}`}
                </Typography>
                {status.lease_expires_at && (
                    <Typography variant="body2" color="text.secondary">
                        Si nadie lo renueva, vuelve a su estado seguro a las {clock(status.lease_expires_at)}.
                    </Typography>
                )}
                {status.supervised === false && status.controllable && (
                    <Typography variant="caption" color="text.secondary">
                        Sin estado seguro: si la plataforma deja de responder, queda como está.
                    </Typography>
                )}
            </Stack>

            {canOperate && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap" }} useFlexGap>
                    <Button size="small" variant="outlined" disabled={saving || equipment.locked_out || !!trippedLimit}
                            onClick={() => run(() => deviceServices.holdActuator(deviceId, "ON", MANUAL_MINUTES), "No se pudo encender.")}
                            sx={{ textTransform: "none" }}>
                        {status.supervised ? `Encender ${MANUAL_MINUTES} min` : "Encender"}
                    </Button>
                    <Button size="small" variant="outlined" disabled={saving}
                            onClick={() => run(() => deviceServices.holdActuator(deviceId, "OFF", MANUAL_MINUTES), "No se pudo apagar.")}
                            sx={{ textTransform: "none" }}>
                        {status.supervised ? `Apagar ${MANUAL_MINUTES} min` : "Apagar"}
                    </Button>
                    {status.manual_hold_until && (
                        <Button size="small" disabled={saving} sx={{ textTransform: "none" }}
                                onClick={() => run(() => deviceServices.releaseActuator(deviceId), "No se pudo volver a automático.")}>
                            Volver a automático (manual hasta las {clock(status.manual_hold_until)})
                        </Button>
                    )}
                </Stack>
            )}

            {canOperate && (
                <TextField select size="small" fullWidth sx={{ mt: 1.5 }} disabled={saving}
                           label="Estado seguro"
                           helperText="Lo que hace la salida si nadie la sostiene: arriendo vencido, sensor sin datos o plataforma caída."
                           SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                           value={status.fail_safe_state || ""}
                           onChange={(event) => run(() => deviceServices.updateActuatorFailSafe(deviceId, event.target.value), "No se pudo cambiar el estado seguro.")}>
                    <MenuItem value="OFF">Apagar</MenuItem>
                    <MenuItem value="ON">Encender</MenuItem>
                    <MenuItem value="">Sin supervisión</MenuItem>
                </TextField>
            )}

            {(loop || (canOperate && loopCapable)) && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Control automático</Typography>
                    {loop ? (
                        <>
                            <Typography variant="body2">
                                Controlada por <b>«{loop.name}»</b>{loop.enabled ? "" : " (pausado)"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{describeControlLoop(loop)}</Typography>
                            <Button size="small" sx={{ mt: 0.5, textTransform: "none" }}
                                    onClick={() => navigate(`/automations/rules/control_loop/${loop.id}`, { state: backHere })}>
                                Ver en Automatizaciones
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary">Ninguna automatización la mantiene en rango.</Typography>
                            <Button size="small" sx={{ mt: 0.5, textTransform: "none" }}
                                    onClick={() => navigate(`/automations/rules/new/control_loop?actuator_id=${deviceId}`, { state: backHere })}>
                                Crear control «Mantener en rango»
                            </Button>
                        </>
                    )}
                </Box>
            )}

            {(limits.length > 0 || (canOperate && loopCapable)) && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Límites de seguridad</Typography>
                    {limits.length ? limits.map((limit) => (
                        <Stack key={`limit-${limit.id}`} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" color={limit.tripped ? "error.main" : "text.primary"}>
                                «{limit.name}» · {limit.condition}{limit.tripped ? " · activo" : ""}{limit.enabled ? "" : " · pausado"}
                            </Typography>
                            <Button size="small" sx={{ textTransform: "none", flexShrink: 0 }}
                                    onClick={() => navigate(`/automations/rules/safety_limit/${limit.id}`, { state: backHere })}>
                                Ver
                            </Button>
                        </Stack>
                    )) : (
                        <Typography variant="body2" color="text.secondary">Ningún límite protege esta salida.</Typography>
                    )}
                    {canOperate && loopCapable && (
                        <Button size="small" sx={{ mt: 0.5, textTransform: "none" }}
                                onClick={() => navigate(`/automations/rules/new/safety_limit?actuator_id=${deviceId}`, { state: backHere })}>
                            Agregar límite de seguridad
                        </Button>
                    )}
                </Box>
            )}

            {!!status.history?.length && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Historial</Typography>
                    <Stack spacing={0.5}>
                        {status.history.map((entry) => (
                            <Stack key={`${entry.kind}-${entry.time}-${entry.type}`} direction="row" spacing={1} alignItems="flex-start">
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, pt: 0.2 }}>{clock(entry.time)}</Typography>
                                <Typography variant="body2" color={entry.kind === "event" || entry.status === "failed" ? "warning.main" : "text.primary"}>
                                    {entry.text}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}
        </Paper>
    );
};

export default ActuatorControlSection;
